import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar, X, Sparkles, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORE = "cit-admin-key";

const CATEGORIES = [
  "creator-tips", "youtube-growth", "youtube-seo",
  "monetization", "creator-economy", "analytics", "artificial-intelligence",
];

const SAMPLE_TOPICS = [
  "How to grow a faceless YouTube channel in 2026",
  "Best CPM niches for new YouTube creators in 2026",
  "YouTube Shorts vs long-form: what to publish first",
  "How to write YouTube titles that get 10x more clicks",
  "The complete YouTube retention playbook for 2026",
  "How to price your first brand deal as a small creator",
  "AI tools every YouTuber should use in 2026",
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function BlogScheduler() {
  const [key] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [topics, setTopics] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [startDate, setStartDate] = useState(todayISO());
  const [publishTime, setPublishTime] = useState("09:00");
  const [category, setCategory] = useState("");
  const [intervalDays, setIntervalDays] = useState(1);
  const [busy, setBusy] = useState(false);
  const [scheduled, setScheduled] = useState([]);

  const loadScheduled = async () => {
    if (!key) return;
    try {
      const r = await fetch(`${API}/api/admin/blog/scheduled`, {
        headers: { "X-Admin-Key": key },
      });
      const d = await r.json();
      setScheduled(d.posts || []);
    } catch (err) {
      console.debug("load scheduled failed", err);
    }
  };

  useEffect(() => { loadScheduled(); /* eslint-disable-line */ }, []);

  const suggestTopics = async () => {
    if (!key) { toast.error("Missing admin key"); return; }
    setSuggesting(true);
    try {
      const r = await fetch(`${API}/api/admin/blog/suggest-topics`, {
        method: "POST",
        headers: { "X-Admin-Key": key, "Content-Type": "application/json" },
        body: JSON.stringify({ count: 30, category: category || null }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `${r.status}`);
      }
      const d = await r.json();
      const list = (d.topics || []).filter(Boolean);
      if (!list.length) { toast.error("AI returned no topics — try again"); return; }
      setTopics(list.join("\n"));
      toast.success(`Suggested ${list.length} topics — review and click Generate`);
    } catch (err) {
      toast.error(err.message || "Failed to suggest topics");
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    const list = topics.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one topic"); return; }
    if (list.length > 30) { toast.error("Max 30 topics per batch"); return; }

    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/blog/generate`, {
        method: "POST",
        headers: { "X-Admin-Key": key, "Content-Type": "application/json" },
        body: JSON.stringify({
          topics: list,
          start_date: startDate,
          publish_time: publishTime,
          category: category || null,
          interval_days: Number(intervalDays) || 1,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `${r.status}`);
      }
      const d = await r.json();
      const ok = d.posts.filter((p) => p.ok).length;
      const failed = d.posts.length - ok;
      toast.success(`Generated ${ok} post${ok === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
      setTopics("");
      await loadScheduled();
    } catch (err) {
      toast.error(err.message || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled post?")) return;
    try {
      const r = await fetch(`${API}/api/admin/blog/scheduled/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Key": key },
      });
      if (!r.ok) throw new Error("Could not cancel");
      toast.success("Cancelled");
      setScheduled((s) => s.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

  const dedupe = async () => {
    if (!window.confirm("Remove duplicate scheduled posts (kept: the earliest of each title)? Published posts are not affected.")) return;
    try {
      const r = await fetch(`${API}/api/admin/blog/dedupe`, {
        method: "POST",
        headers: { "X-Admin-Key": key },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `${r.status}`);
      }
      const d = await r.json();
      toast.success(`Removed ${d.removed} duplicate${d.removed === 1 ? "" : "s"}`);
      await loadScheduled();
    } catch (err) {
      toast.error(err.message || "Dedupe failed");
    }
  };

  const rebuildCovers = async () => {
    if (!window.confirm("Regenerate AI cover images for posts that lost them (e.g. after a redeploy)? This runs Nano Banana for each missing cover — takes ~10s per post.")) return;
    const t = toast.loading("Rebuilding covers…");
    try {
      const r = await fetch(`${API}/api/admin/blog/covers/rebuild`, {
        method: "POST",
        headers: { "X-Admin-Key": key, "Content-Type": "application/json" },
        body: JSON.stringify({ only_missing: true, limit: 30 }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `${r.status}`);
      }
      const d = await r.json();
      toast.success(`Rebuilt ${d.rebuilt} cover${d.rebuilt === 1 ? "" : "s"}`, { id: t });
      await loadScheduled();
    } catch (err) {
      toast.error(err.message || "Rebuild failed", { id: t });
    }
  };

  return (
    <div className="mt-12 space-y-6" data-testid="blog-scheduler">
      <div>
        <div className="overline">Content operations</div>
        <h2 className="mt-1 text-2xl font-black tracking-tight">Blog scheduler</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Give GPT-5.2 up to 30 topics (one per line) — or let AI suggest a full month of SEO-optimized topics in one click. Posts are scheduled starting from the date/time below and go live automatically.
        </p>
      </div>

      <form onSubmit={generate} className="card-flat p-5 grid gap-4">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="overline text-[10px]">Topics · one per line · max 30</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="blog-topics-load-samples"
                onClick={() => setTopics(SAMPLE_TOPICS.join("\n"))}
                className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:underline"
              >
                Load 7 samples
              </button>
              <button
                type="button"
                data-testid="blog-topics-suggest-ai"
                onClick={suggestTopics}
                disabled={suggesting || !key}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {suggesting ? "Suggesting…" : "Auto-generate 30 topics with AI"}
              </button>
            </div>
          </div>
          <Textarea
            data-testid="blog-topics"
            value={topics}
            onChange={(e) => setTopics(e.target.value)}
            rows={6}
            placeholder={`e.g. How to grow a faceless YouTube channel in 2026\ne.g. Best CPM niches for new creators\ne.g. YouTube Shorts vs long-form: what to publish first\n\n(Placeholder text — use the buttons above to load samples or auto-generate 30 topics with AI, or type your own, one per line.)`}
            className="mt-1 font-mono text-sm"
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="overline text-[10px]">Start date</label>
            <Input data-testid="blog-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="overline text-[10px]">Publish time (UTC)</label>
            <Input data-testid="blog-publish-time" type="time" value={publishTime} onChange={(e) => setPublishTime(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="overline text-[10px]">Days between posts</label>
            <Input data-testid="blog-interval" type="number" min={1} max={30} value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="overline text-[10px]">Category (optional)</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-transparent text-sm"
              data-testid="blog-category"
            >
              <option value="">Auto-detect</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground font-mono">
            Each post takes 30–90s to generate. Please wait for the batch to complete.
          </p>
          <Button type="submit" disabled={busy || !key} className="btn-primary h-10 px-5" data-testid="blog-generate-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {busy ? "Generating…" : "Generate & schedule"}
          </Button>
        </div>
      </form>

      {/* Scheduled list */}
      <div className="card-flat p-5" data-testid="blog-scheduled-list">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary/80" />
            <div className="overline">Schedule ({scheduled.length})</div>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={rebuildCovers}
              data-testid="blog-rebuild-covers"
              className="text-xs font-mono text-muted-foreground hover:text-primary"
            >
              Rebuild missing covers
            </button>
            <button
              type="button"
              onClick={dedupe}
              data-testid="blog-dedupe"
              className="text-xs font-mono text-muted-foreground hover:text-destructive"
            >
              Remove duplicates
            </button>
            <button onClick={loadScheduled} className="text-xs font-mono text-muted-foreground hover:text-primary">Refresh</button>
          </div>
        </div>

        {scheduled.length === 0 ? (
          <div className="text-sm text-muted-foreground">No posts scheduled yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground/70 text-xs font-mono">
                <tr className="text-left">
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Publish at (UTC)</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {scheduled.map((p) => {
                  const isScheduled = p.status === "scheduled";
                  return (
                    <tr key={p.id} className="border-t border-border/50 align-top" data-testid={`sched-row-${p.id}`}>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] font-mono px-2 py-0.5 rounded ${isScheduled ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{fmt(p.publish_at)}</td>
                      <td className="py-2 pr-3">
                        <div className="font-semibold">{p.title}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-md">{p.excerpt}</div>
                      </td>
                      <td className="py-2 pr-3 text-xs font-mono text-muted-foreground">{p.category}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {p.status === "published" ? (
                          <a
                            href={`/blog/${p.slug}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          >
                            <Send className="w-3 h-3" /> View
                          </a>
                        ) : (
                          <button
                            onClick={() => cancel(p.id)}
                            className="text-xs text-rose-500 hover:underline inline-flex items-center gap-1"
                            data-testid={`sched-cancel-${p.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
